import { z } from "zod"
import { requestNativeCapability, supportsNativeCapability, waitForNativeBridge } from "./native-capabilities.ts"

const api = (path: string): string => `${process.env["NEXT_PUBLIC_API_BASE_URL"] ?? "/api"}${path}`
const sessionSchema = z.strictObject({ firebaseUid: z.string().min(1), provider: z.enum(["GOOGLE", "APPLE"]).nullable() })
const deviceSchema = z.strictObject({ deviceId: z.uuid(), token: z.string().min(20).max(4096), appVersion: z.string().min(1).max(50), platform: z.enum(["ANDROID", "IOS"]) })
export const getNativeWebSession = async () => {
  const response = await fetch(api("/web/native/session"), { credentials: "include", cache: "no-store" })
  if (response.status === 401) return null
  if (!response.ok) throw new Error("WEB_NATIVE_SESSION_UNAVAILABLE")
  const csrf = response.headers.get("x-cashhub-csrf-token")
  if (csrf === null) throw new Error("WEB_NATIVE_CSRF_UNAVAILABLE")
  return { ...sessionSchema.parse(await response.json()), csrf }
}
let registeredDocumentIdentity: string | null = null
export const invalidateNativePushRegistration = (): void => {
  registeredDocumentIdentity = null
}
export const registerNativePush = async (force = false): Promise<void> => {
  const bridge = await waitForNativeBridge()
  if (!supportsNativeCapability("getPushDevice", bridge)) return
  // Ask permission only after Web has authenticated the member.
  const session = await getNativeWebSession()
  if (session === null) {
    registeredDocumentIdentity = null
    return
  }
  const identity = `${bridge.nonce}:${session.firebaseUid}`
  if (!force && registeredDocumentIdentity === identity) return
  const device = deviceSchema.parse(await requestNativeCapability("getPushDevice"))
  const response = await fetch(api("/web/native/device"), { method: "PUT", credentials: "include", headers: { "content-type": "application/json", "x-cashhub-csrf": session.csrf }, body: JSON.stringify(device) })
  if (response.status === 204) registeredDocumentIdentity = identity
  if (!response.ok && response.status !== 401) throw new Error("WEB_NATIVE_PUSH_REGISTRATION_FAILED")
}
export const logoutNativeWebSession = async (): Promise<boolean> => {
  registeredDocumentIdentity = null
  const session = await getNativeWebSession()
  if (session === null) {
    const cleared = await fetch(api("/identity/logout"), { method: "POST", credentials: "include" })
    return cleared.status === 204
  }
  const response = await fetch(api("/web/native/logout"), { method: "POST", credentials: "include", headers: { "x-cashhub-csrf": session.csrf } })
  return response.status === 204
}

/** SDK reauthentication never exchanges/replaces the already authenticated Web member. */
export const ensureNativePhoneIdentity = async (): Promise<string | null> => {
  const bridge = await waitForNativeBridge()
  if (!supportsNativeCapability("getIdentity", bridge)) return null
  const session = await getNativeWebSession()
  if (session === null || session.provider === null) throw new Error("IDENTITY_REQUIRED")
  let identity = await requestNativeCapability("getIdentity")
  if (identity["firebaseUid"] !== session.firebaseUid) {
    await requestNativeCapability("signIn", { provider: session.provider.toLowerCase() })
    identity = await requestNativeCapability("getIdentity")
  }
  if (identity["firebaseUid"] !== session.firebaseUid) {
    await requestNativeCapability("signOut")
    throw new Error("IDENTITY_REQUIRED")
  }
  return session.firebaseUid
}
